import React from 'react';
import { Box } from 'src/design/atoms/Box';
import { Card } from 'src/design/atoms/Card';
import { Tabs } from 'src/design/molecules/Tabs';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { BaseTemplateDetails } from './components/BaseTemplateDetails';
import { LaborRates } from './components/LaborRates';
import { UnitPricing } from './components/UnitPricing';
import { ChangeTemplateDto, RateSheetDto } from 'src/api/FiretrolApiClient';
import {
  useCreateChangeTemplate,
  useUpdateChangeTemplate,
} from 'src/api/queries/changeTemplates/useChangeTemplateMutations';
import { useNavigate } from 'react-router-dom';

interface ChangeTemplateFormValues {
  title: string;
  officeId: number;
  asOfDate: Date;
  isActive: boolean;
  designSheets: RateSheetDto[];
  laborSheets: RateSheetDto[];
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required').min(1, 'Title is required'),
  officeId: Yup.number()
    .required('Office is required')
    .min(0, 'Office is required'),
  asOfDate: Yup.date().required('As Of date is required'),
  designSheets: Yup.array().min(
    1,
    'At least one design rate sheet is required',
  ),
  laborSheets: Yup.array().min(1, 'At least one labor rate sheet is required'),
});

interface ChangeTemplateFormProps {
  initialValues?: ChangeTemplateDto;
}

export const ChangeTemplateForm: React.FC<ChangeTemplateFormProps> = ({
  initialValues,
}) => {
  const [tab, setTab] = React.useState<string | null>('labor');
  const navigate = useNavigate();
  const isEditing = !!initialValues?.id;
  const createTemplate = useCreateChangeTemplate();
  const updateTemplate = useUpdateChangeTemplate();

  const formik = useFormik<ChangeTemplateFormValues>({
    initialValues: {
      title: initialValues?.title || '',
      officeId: initialValues?.officeId ?? -1,
      asOfDate: initialValues?.asOfDate
        ? new Date(initialValues.asOfDate)
        : new Date(),
      isActive: initialValues?.isActive ?? true,
      designSheets: initialValues?.designSheets || [],
      laborSheets: initialValues?.laborSheets || [],
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      const payload = {
        title: values.title,
        officeId: values.officeId,
        asOfDate: values.asOfDate,
        isActive: values.isActive,
        designSheets: values.designSheets,
        laborSheets: values.laborSheets,
      };

      if (isEditing && initialValues?.id) {
        await updateTemplate
          .mutateAsync({ id: initialValues.id, request: payload })
          .catch(() => null);
        return;
      }
      const result = await createTemplate
        .mutateAsync(payload)
        .catch(() => null);
      if (result) navigate(`/settings/changes/template/edit/${result.id}`);
    },
  });

  const { values, setFieldValue, isValid } = formik;
  const isSaving = createTemplate.isPending || updateTemplate.isPending;

  return (
    <>
      <Box mb="md">
        <BaseTemplateDetails
          titleValue={values.title}
          onTitleValueChange={(v) => setFieldValue('title', v)}
          dateValue={values.asOfDate}
          onDateValueChange={(v) => setFieldValue('asOfDate', v)}
          activeValue={values.isActive}
          onActiveValueChange={(v) => setFieldValue('isActive', v)}
          officeValue={values.officeId}
          onOfficeValueChange={(value) => setFieldValue('officeId', value)}
          templateId={initialValues?.id || null}
          canSave={isValid}
          isSaving={isSaving}
          onSaveClick={formik.submitForm}
        />
      </Box>
      <Card>
        <Tabs value={tab} onChange={setTab}>
          <Tabs.List>
            <Tabs.Tab value="labor">Labor Rates</Tabs.Tab>
            <Tabs.Tab value="design">Design Rates</Tabs.Tab>
            <Tabs.Tab value="unit">Unit Prices</Tabs.Tab>
            <Tabs.Tab value="material">Material & Labor</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="labor">
            <h3>Labor Rates</h3>
            <LaborRates
              rateType={'labor'}
              initialRateSheets={values.laborSheets}
              onChange={(sheets) => setFieldValue('laborSheets', sheets)}
            />
          </Tabs.Panel>
          <Tabs.Panel value="design">
            <h3>Design Rates</h3>
            <LaborRates
              rateType={'design'}
              initialRateSheets={values.designSheets}
              onChange={(sheets) => setFieldValue('designSheets', sheets)}
            />
          </Tabs.Panel>
          <Tabs.Panel value="unit">
            <h3>Unit Prices</h3>
            <UnitPricing />
          </Tabs.Panel>
          <Tabs.Panel value="material">
            <h3>Material & Labor</h3>
          </Tabs.Panel>
        </Tabs>
      </Card>
    </>
  );
};
